<template>
  <div>
    
    <v-container v-if="!collected" fluid class="donate-page pa-0">
      <!-- Progress Dialog -->
      <v-dialog v-model="dialogProgress" persistent width="320">
        <v-card class="rounded-xl" color="primary" dark>
          <v-card-text class="pa-5">
            <div class="font-weight-bold mb-2">Processing...</div>
            <div class="text-body-2 mb-3">Please stand by</div>
            <v-progress-linear indeterminate color="white" />
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-row justify="center" no-gutters>
        <v-col cols="12" sm="10" md="7" lg="5" class="pa-4">
          <!-- Header -->
          <v-card v-if="Campaigns" class="card-soft mb-3" outlined>
            <v-card-text class="pa-4">
              <div class="d-flex align-center">
                <div class="min-w-0">
                  <div class="text-subtitle-1 font-weight-bold text-truncate">
                    {{ Campaigns.name || "Donation" }}
                  </div>
                  <div class="text-caption grey--text text--darken-1">
                    Secure donation, receipt sent by email
                  </div>
                </div>
                <v-spacer />
                <v-chip color="green lighten-4" class="font-weight-bold" label>
                  <v-icon left small>mdi-lock</v-icon>
                  Secure
                </v-chip>
              </div>
            </v-card-text>
          </v-card>

          <!-- Step Pills -->
          <div class="step-pills mb-3">
            <div :class="['pill', step >= 1 ? 'active' : '']">1 Amount</div>
            <div class="pill-divider"></div>
            <div :class="['pill', step >= 2 ? 'active' : '']">2 Info</div>
            <div class="pill-divider"></div>
            <div :class="['pill', step >= 3 ? 'active' : '']">3 Pay</div>
          </div>

          <!-- MAIN CARD -->
          <v-card class="card-soft" outlined>
            <!-- STEP 1: AMOUNT -->
            <div v-show="step === 1" class="pa-4">
              <div class="text-subtitle-1 font-weight-bold mb-1">
                Choose an amount
              </div>
              <div class="text-body-2 grey--text text--darken-1 mb-4">
                Tap a quick amount or enter a custom value.
              </div>

              <!-- Quick amounts -->
              <div class="amount-grid">
                <v-btn
                  v-for="(val, idx) in preDefinedValue"
                  :key="val"
                  class="amount-btn"
                  :outlined="selectedAmount !== idx"
                  :color="selectedAmount === idx ? 'primary' : ''"
                  @click="selectedAmount = idx"
                >
                  {{ val | currency }}
                </v-btn>
              </div>

              <!-- Custom amount -->
              <v-card class="soft-box mt-4" flat>
                <v-card-text class="pa-3">
                  <div class="text-caption grey--text text--darken-1 mb-1">
                    Custom amount
                  </div>
                  <v-text-field
                    v-model="amount"
                    prefix="$"
                    outlined
                    dense
                    hide-details
                    type="number"
                    inputmode="decimal"
                    label="Donation Amount"
                    class="mb-1"
                  />
                  <div class="text-caption grey--text text--darken-1">
                    Minimum online donation is $10
                  </div>
                </v-card-text>
              </v-card>

              <div class="d-flex align-center mt-4">
                <v-btn text @click="$router.go(-1)">Back</v-btn>
                <v-spacer />
                <v-btn
                  color="primary"
                  rounded
                  class="px-6"
                  :disabled="!allowStep2"
                  @click="infoStep"
                >
                  Next
                </v-btn>
              </div>
            </div>

            <!-- STEP 2: INFO -->
            <div v-show="step === 2" class="pa-4">
              <div class="text-subtitle-1 font-weight-bold mb-1">
                Your information
              </div>
              <div class="text-body-2 grey--text text--darken-1 mb-4">
                We’ll email your receipt.
              </div>

              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="Donor.name"
                    :error-messages="nameErrors"
                    :hide-details="nameErrors.length === 0"
                    outlined
                    dense
                    label="Name"
                  />
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="Donor.phoneNumber"
                    :error-messages="phoneNumberErrors"
                    :hide-details="phoneNumberErrors.length === 0"
                    outlined
                    dense
                    type="tel"
                    inputmode="tel"
                    label="Phone Number"
                  />
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="Donor.email"
                    :error-messages="emailErrors"
                    :hide-details="emailErrors.length === 0"
                    outlined
                    dense
                    type="email"
                    inputmode="email"
                    label="Email Address"
                  />
                </v-col>
              </v-row>

              <div class="d-flex align-center mt-2">
                <v-btn text @click="step = 1">Back</v-btn>
                <v-spacer />
                <v-btn
                  color="primary"
                  rounded
                  class="px-6"
                  @click="paymentInfo"
                >
                  Next
                </v-btn>
              </div>
            </div>

            <!-- STEP 3: PAYMENT -->
            <div v-show="step === 3" class="pa-4">
              <div class="text-subtitle-1 font-weight-bold mb-1">Payment</div>
              <div class="text-body-2 grey--text text--darken-1 mb-4">
                Enter your card details to complete the donation.
              </div>

              <CreditCard
                v-if="step === 3"
                @submit="infoCollect"
                :localCollectOnly="true"
                :donor="Donor"
                :public="true"
                :donationCategoryId="Business.DefaultDonationId"
              />

              <div class="text-body-2 mt-4" v-if="Business">
                By clicking “Donate”, you authorize an electronic fund transfer
                to {{ Business.name }}.
              </div>

              <div class="d-flex align-center mt-4">
                <v-btn text @click="step = 2">Back</v-btn>
                <v-spacer />
                <v-btn
                  color="success"
                  rounded
                  class="px-7"
                  :disabled="loading"
                  :loading="loading"
                  @click="submit"
                >
                  Donate {{ totalDonationAmount | currency }}
                </v-btn>
              </div>
            </div>
          </v-card>

          <!-- Sticky bottom bar (mobile) -->
          <div class="sticky-bar" v-if="$vuetify.breakpoint.xsOnly">
            <div class="sticky-inner">
              <div class="min-w-0">
                <div class="text-caption grey--text text--darken-1">Total</div>
                <div class="font-weight-bold">
                  {{ totalDonationAmount | currency }}
                </div>
              </div>
              <v-spacer />
              <v-btn
                v-if="step === 1"
                color="primary"
                rounded
                class="px-6"
                :disabled="!allowStep2"
                @click="infoStep"
              >
                Next
              </v-btn>

              <v-btn
                v-else-if="step === 2"
                color="primary"
                rounded
                class="px-6"
                @click="paymentInfo"
              >
                Next
              </v-btn>

              <v-btn
                v-else
                color="success"
                rounded
                class="px-6"
                :disabled="loading"
                :loading="loading"
                @click="submit"
              >
                Donate
              </v-btn>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- SUCCESS (keep yours, or we can modernize too) -->
    <v-container v-else :fluid="$vuetify.breakpoint.xsOnly" class="page-wrap">
      <v-row justify="center" no-gutters>
        <v-col cols="12" sm="10" md="8" lg="6" class="pa-4">
          <v-card class="card-soft" outlined>
            <v-card-text class="pa-6">
              <lottie
                :height="200"
                :options="defaultOptions"
                name="receiptCheck"
                v-on:animCreated="handleAnimation"
                @onComplete="showHomeButton = true"
              />
              <div class="text-center">
                <div class="text-h5 font-weight-bold">All Set</div>
                <div class="mt-3 text-body-2">
                  The Prophet, upon be peace, said: “The believer’s shade on the
                  Day of Resurrection will be his charity.”
                  <span class="font-weight-bold">Al-Tirmidhi</span>
                </div>
              </div>

              <div class="text-center mt-5" v-if="showHomeButton">
                <v-btn
                  block
                  large
                  @click="$router.go(-1)"
                  color="success"
                  rounded
                >
                  Home
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import donationService from "@/modules/Campaign/service.js";
import CreditCard from "@/modules/PaymentMethod/components/CreditCard.vue";
import Lottie from "@/components/lottie.vue";
import * as animationData from "@/assets/lottie/1708-success.json";
const DONOR_STORAGE_KEY = "taleem_public_donor_v1";
const { validationMixin } = require("vuelidate");
const { required, email } = require("vuelidate/lib/validators");

export default {
  name: "collectDonationByCampaign",
  data() {
    return {
      donorSaveTimer: null,
      dialogProgress: false,
      donationLimit: false,
      paidInFullAmount: 0,
      allRowTotal: 0,
      donationSelectedAmount: 0,
      dialog: false,
      selectedRow: null,
      Donor: {
        name: null,
        phoneNumber: null,
        email: null,
      },
      menu1: false,
      dateFormatted: null,
      startingDate: this.$moment().add(1, "day"),
      minDate: null,
      donationObj: {
        paymentType: "card",
        paymentCycle: "monthly",
        accountType: "personal",
        startingDate: this.$moment().add(1, "day"),
      },
      group: [],
      collected: false,
      card: null,
      showHomeButton: false,
      showSecondCollection: true,
      fridays: ["26", "52", "78", "90", "115"],
      months: ["6", "12", "18", "24", "36"],
      numberOfMonth: "12",
      numberOfFriday: "52",
      loading: false,
      feeAmount: 0,
      allowStep2: false,
      totalDonationAmount: 0,
      step: 1,
      selectedAmount: 0,
      amount: 10,
      paymentCycle: "oneTime",
      Campaigns: null,
      Business: null,
      addFee: false,
      addFeeSecond: false,
      preDefinedValue: ["10", "20", "50", "100", "150", "200"],
      defaultOptions: {
        animationData: animationData.default,
        loop: false,
        autoplay: true,
      },
    };
  },
  created() {
    this.loadDonorFromLocal();
    this.loadCampaign();
  },
  mixins: [validationMixin],
  validations: {
    Donor: {
      name: {
        required,
      },
      phoneNumber: {
        required,
      },
      email: {
        required,
        email,
      },
    },
  },
  components: {
    CreditCard,
    Lottie,
  },
  computed: {
    nameErrors() {
      const errors = [];
      if (!this.$v.Donor.name.$dirty) return errors;
      if (!this.$v.Donor.name.required) errors.push("First Name is required");
      return errors;
    },
    phoneNumberErrors() {
      const errors = [];
      if (!this.$v.Donor.phoneNumber.$dirty) return errors;
      if (!this.$v.Donor.phoneNumber.required)
        errors.push("Phone Number is required");
      return errors;
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.Donor.email.$dirty) return errors;
      if (!this.$v.Donor.email.required)
        errors.push("Email Address is required");
      if (!this.$v.Donor.email.email) errors.push("Email is not valid");
      return errors;
    },
  },
  watch: {
    Donor: {
      deep: true,
      handler() {
        clearTimeout(this.donorSaveTimer);
        this.donorSaveTimer = setTimeout(() => {
          this.saveDonorToLocal();
        }, 250);
      },
    },
    allRowTotal(val) {
      if (val > 0) {
        this.allowStep2 = true;
      } else {
        this.allowStep2 = false;
      }
    },
    dialog(val) {
      if (val) {
        if (this.selectedRow.received) {
          this.paidInFullAmount =
            parseFloat(this.selectedRow.amount) -
            parseFloat(this.selectedRow.received);
        } else {
          this.paidInFullAmount = parseFloat(this.selectedRow.amount);
        }
      }
    },
    addFeeSecond() {
      console.log("click me");
      this.setDonationAmount();
    },
    addFee(val) {
      if (val) {
        this.showSecondCollection = false;
      } else {
        this.showSecondCollection = true;
      }
      this.setDonationAmount();
    },
    amount() {
      console.log("saqib test");
      this.setDonationAmount();
    },
    selectedAmount() {
      this.showOtherInput();
    },
    donationSelectedAmount(val) {
      // console.log('donationSelectedAmount', val)
      this.selectedRow.donationAmount = this.preDefinedValue[val];
    },
  },
  methods: {
    saveDonorToLocal() {
      try {
        const payload = {
          name: (this.Donor?.name || "").trim(),
          phoneNumber: (this.Donor?.phoneNumber || "").toString().trim(),
          email: (this.Donor?.email || "").trim(),
          updatedAt: new Date().toISOString(),
        };

        // don’t save empty junk
        if (!payload.name && !payload.phoneNumber && !payload.email) return;

        localStorage.setItem(DONOR_STORAGE_KEY, JSON.stringify(payload));
      } catch (e) {
        // ignore storage errors
      }
    },

    loadDonorFromLocal() {
      try {
        const raw = localStorage.getItem(DONOR_STORAGE_KEY);
        if (!raw) return;

        const saved = JSON.parse(raw) || {};

        // only fill missing fields
        if (!this.Donor.name && saved.name) this.Donor.name = saved.name;
        if (!this.Donor.phoneNumber && saved.phoneNumber)
          this.Donor.phoneNumber = saved.phoneNumber;
        if (!this.Donor.email && saved.email) this.Donor.email = saved.email;
      } catch (e) {
        // ignore parse errors
      }
    },
    reCalculateFee() {
      const monthlyValue = this.allRowTotal / 12;
      if (this.donationLimit) {
        this.feeAmount = (
          (parseFloat(this.allRowTotal) * 3.4) / 100 +
          0.3
        ).toFixed(2);
      } else {
        if (this.donationObj.paymentCycle === "monthly") {
          this.feeAmount = (
            (parseFloat(monthlyValue) * 3.4) / 100 +
            0.3
          ).toFixed(2);
        } else {
          this.feeAmount = (
            (parseFloat(this.allRowTotal) * 3.4) / 100 +
            0.3
          ).toFixed(2);
        }
      }

      if (this.addFee || this.addFeeSecond) {
        if (this.donationLimit) {
          this.totalDonationAmount =
            parseFloat(this.allRowTotal) + parseFloat(this.feeAmount);
        } else {
          if (this.donationObj.paymentCycle === "monthly") {
            this.totalDonationAmount =
              parseFloat(monthlyValue) + parseFloat(this.feeAmount);
          } else {
            this.totalDonationAmount =
              parseFloat(this.allRowTotal) + parseFloat(this.feeAmount);
          }
        }
      } else {
        if (this.donationLimit) {
          this.totalDonationAmount = parseFloat(this.allRowTotal);
        } else {
          this.totalDonationAmount = parseFloat(monthlyValue);
        }
      }
    },
    getFormattedDay(date) {
      const dayOfMonth = this.$moment(date).date();
      const suffix = this.getDaySuffix(dayOfMonth);
      return dayOfMonth + suffix;
    },
    getDaySuffix(day) {
      if (day >= 11 && day <= 13) {
        return "th";
      }
      switch (day % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    },
    formatDate(date) {
      if (!date) return null;

      const [year, month, day] = date.split("-");
      return `${month}/${day}/${year}`;
    },
    parseDate(date) {
      if (!date) return null;

      const [month, day, year] = date.split("/");
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    },
    plus(item) {
      item.qty += 1;
      // console.log('this.group', this.group)
      this.setDonationAmount();
    },
    minus(item) {
      item.qty -= 1;
      // console.log('this.group', this.group)
      this.setDonationAmount();
    },
    paidInFull() {
      this.selectedRow.donationAmount = this.paidInFullAmount;
      this.submitDay();
    },
    handleAnimation(anim) {
      this.anim = anim;
    },
    calculateReceivedingPercentage(receivedingAmount, totalAmount) {
      // Calculate the percentage
      let receive = 0;
      if (receivedingAmount) {
        receive = receivedingAmount;
      }
      const percentage = (receive / totalAmount) * 100;

      // Return the percentage, rounded to 2 decimal places
      return parseFloat(percentage.toFixed(0));
    },
    isActive(item) {
      let value = false;
      if (item.donationAmount > 0) {
        value = true;
      }
      return value;
    },
    reset() {
      this.donationSelectedAmount = 0;
      // this.selectedRow.donationAmount = 10
      this.preDefinedValue = ["10", "20", "50", "100", "150", "200"];
    },
    close() {
      this.reset();
      this.dialog = false;
      this.setDonationAmount();
    },
    submitDay() {
      // console.log('submit day', this.selectedRow)
      // console.log('group', this.group)
      this.group.map((cateogry) => {
        cateogry.items.map((row) => {
          if (row.id === this.selectedRow.id) {
            // console.log('founded', row)
            row.donationAmount = this.selectedRow.donationAmount;
          }
        });
      });
      this.close();
      this.$forceUpdate();
    },
    openRow(item) {
      this.selectedRow = JSON.parse(JSON.stringify(item));
      this.selectedRow.donationAmount = 10;
      // console.log('this.selectedRow', item)
      // console.log('this.calculateReceivedingPercentage', this.calculateReceivedingPercentage(item.received, item.amount))
      if (
        this.calculateReceivedingPercentage(item.received, item.amount) < 100
      ) {
        this.dialog = !this.dialog;
      } else {
        this.$swal.fire({
          title: item.name,
          text: "Reserved and paid in full",
          icon: "info",
        });
      }
    },
    groupByCategory(data) {
      const result = [];

      // Create an object to hold grouped items temporarily
      const temp = {};

      // Loop through the data array
      data.CampaignDetails.forEach((item) => {
        const { category, id, name, amount, received } = item;

        // If the category doesn't exist in the temp object, create an array for it
        if (!temp[category]) {
          temp[category] = [];
        }

        // Push the name and amount pair to the category array
        if (this.donationLimit) {
          temp[category].push({
            id,
            name,
            amount,
            received,
            donationAmount: 0,
          });
        } else {
          temp[category].push({ id, name, amount, qty: 0 });
        }
      });

      // Convert temp object to array of objects
      for (const category in temp) {
        result.push({ category, items: temp[category] });
      }

      return result;
    },
    stripHTML(html) {
      const tempElement = document.createElement("div");
      tempElement.innerHTML = html;
      return tempElement.textContent || tempElement.innerText || "";
    },
    infoCollect(val) {
      this.card = val;
    },
    submit() {
      // console.log('submit press')
      try {
        let data = {};
        data.Donor = this.Donor;

        // data.ProductId = this.selectedCategory.id
        // data.productName = this.selectedCategory.name
        this.$events.emit("submitCreditCard");
        // console.log('test', test)
        data.donationLimit = this.donationLimit;
        data.cycle = this.paymentCycle;
        data.numberOfMonth = this.numberOfMonth;
        data.numberOfFriday = this.numberOfFriday;
        data.Donor = this.Donor;
        data.card = this.card;
        data.campaignType = this.Campaigns.type;
        data.selectedRow = [];

        if (this.addFee || this.addFeeSecond) {
          data.processingFeeCollect = true;
        } else {
          data.processingFeeCollect = false;
        }

        data.amount = this.amount;

        data.feeAmount = this.feeAmount;

        if (data.processingFeeCollect) {
          data.finalAmount =
            parseFloat(data.amount) + parseFloat(data.feeAmount);
        } else {
          data.finalAmount = parseFloat(data.amount);
        }

        data.CampaignId = this.Campaigns.id;

        console.log("data collected", this.Campaigns);
        console.log("data collected data", data);

        if (!data.card) {
          return false;
        }
        // this.loadingSubmit = true;
        this.loading = true;
        // this.dialogProgress = true
        donationService
          .publicDonationCreate(this.Business.code, data)
          .then((response) => {
            console.log("response publicSponsorCreate", response);
            // this.loadingSubmit = false;
            this.collected = true;
            this.loading = false;
          })
          .catch((error) => {
            // this.loadingSubmit = false;
            // this.loading = false;
            // this.dialogProgress = false
            console.log("err", error);
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "error",
                title: error.data.message,
              });
          });
      } catch (error) {
        this.dialogProgress = false;
        this.loadingSubmit = false;
        this.loading = false;
        console.log("error adding card3", error);
      }
    },
    paymentInfo() {
      this.$v.Donor.$touch();
      if (this.$v.Donor.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
      } else {
        this.saveDonorToLocal();
        this.step = 3;
      }

      // console.log('this.Campaigns.metadata.donationLimit', this.Campaigns.metadata.donationLimit)
      // if (this.Campaigns.metadata.donationLimit) {
      //   console.log('stop 3')
      //   this.step = 4
      // } else {
      //   console.log('stop 4')
      //   this.step = 3
      // }
    },
    showOtherInput() {
      this.amount = this.preDefinedValue[this.selectedAmount];
    },
    setDonationAmount() {
      if (this.Campaigns.type === "sponsor") {
        if (this.step === 1 && this.group.length) {
          this.allRowTotal = 0;
          this.group.map((category) => {
            category.items.map((row) => {
              if (row.donationAmount) {
                this.allRowTotal += parseFloat(row.donationAmount);
              }
              if (!this.donationLimit && row.qty > 0) {
                this.allRowTotal += parseFloat(row.amount) * row.qty;
              }
            });
          });
        }
        this.feeAmount = 0;
        this.reCalculateFee();
      } else {
        if (this.amount > 0) {
          this.feeAmount = (parseFloat(this.amount) * 3.4) / 100 + 0.3;
          this.allowStep2 = true;
        } else {
          this.feeAmount = 0;
          this.allowStep2 = false;
        }
        if (this.addFee || this.addFeeSecond) {
          this.totalDonationAmount =
            parseFloat(this.amount) + parseFloat(this.feeAmount);
        } else {
          this.totalDonationAmount = parseFloat(this.amount);
        }
      }
    },
    infoStep() {
      this.step = 2;
    },
    loadCampaign() {
      let code = this.$route.params.id;
      console.log("getting code 22", this.$route.params);
      donationService
        .getCampaignById(code)
        .then((response) => {
          console.log("response campaign", response);
          // if (!response.data) {
          //   this.$router.push({ name: "pagenotfound" });
          // }
          this.Campaigns = response.data;
          this.Business = response.data.Business;
          console.log("test", this.Campaigns.metadata.preDefinedValue);
          if (this.Campaigns?.metadata?.preDefinedValue.length) {
            this.preDefinedValue = this.Campaigns.metadata.preDefinedValue;
            this.amount = this.preDefinedValue[0];
          }
          if (
            this.Campaigns &&
            this.Campaigns.metadata &&
            this.Campaigns.metadata.donationLimit
          ) {
            this.donationLimit = true;
          }

          this.group = this.groupByCategory(this.Campaigns);
          // console.log('this.group', this.group)
        })
        .catch((error) => {
          console.log("error getting campaign", error);
          // this.$router.push({ name: "pagenotfound" });
        });
    },
  },
};
</script>

<style scoped>
.selectedRow {
  background: #b2dfdb;
  font-weight: 900 !important;
}

.paidRow {
  background: #e1bee7;
  /* font-weight: 900 !important; */
}

.receiveMoney {
  background: #e8f5e9;
  /* font-weight: 900 !important; */
}
.donate-page {
  background: #f6f7f9;
  min-height: 100vh;
}

.page-wrap {
  padding: 16px;
  padding-bottom: 110px;
}

.card-soft {
  border-radius: 18px;
  overflow: hidden;
}

.rounded-xl {
  border-radius: 18px !important;
}

.min-w-0 {
  min-width: 0;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Step pills */
.step-pills {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.pill {
  flex: 1;
  text-align: center;
  padding: 10px 10px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid rgba(0,0,0,0.06);
  font-weight: 700;
  font-size: 13px;
  color: rgba(0,0,0,0.5);
}

.pill.active {
  background: #e8f0ff;
  border-color: rgba(25,118,210,0.25);
  color: rgba(0,0,0,0.8);
}

.pill-divider {
  width: 10px;
  height: 2px;
  background: rgba(0,0,0,0.12);
  border-radius: 999px;
}

/* Amount buttons */
.amount-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.amount-btn {
  border-radius: 14px !important;
  height: 44px;
  font-weight: 800;
}

/* soft box */
.soft-box {
  background: #ffffff;
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 16px;
}

/* Sticky bar */
.sticky-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 10px 12px;
  z-index: 20;
}

.sticky-inner {
  max-width: 760px;
  margin: 0 auto;
  background: white;
  border-radius: 18px;
  box-shadow: 0 12px 28px rgba(0,0,0,0.18);
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
}
</style>